import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import axios from 'axios';
import './PDFExporter.css';
import API_BASE_URL, { API_ENDPOINTS } from '../config/api';

// OpenSans fontunu jsPDF'e yükle (Türkçe karakter desteği)
const loadOpenSansFont = async (doc) => {
    try {
        // Backend'den base64 olarak almayı dene
        const res = await axios.get(API_ENDPOINTS.FONT_OPENSANS);
        const base64 = res?.data?.base64;
        if (base64 && typeof base64 === 'string') {
            doc.addFileToVFS('OpenSans-Regular.ttf', base64);
            doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
            // Map bold to same file if bold variant not provided
            doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'bold');
            return true;
        }
    } catch (e) {
        console.warn('OpenSans base64 API bulunamadı, public klasörden denenecek.', e?.message || e);
    }

    try {
        // Public klasöründen TTF indirip base64'e çevir (fallback)
        const resp = await fetch('/OpenSans-Regular.ttf');
        if (resp.ok) {
            const blob = await resp.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.byteLength; i += 1) {
                binary += String.fromCharCode(bytes[i]);
            }
            const base64FromTtf = btoa(binary);
            doc.addFileToVFS('OpenSans-Regular.ttf', base64FromTtf);
            doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'normal');
            doc.addFont('OpenSans-Regular.ttf', 'OpenSans', 'bold');
            return true;
        }
    } catch (error) {
        console.error('OpenSans font loading failed:', error);
    }
    return false;
};

// Güvenli font ayarlama fonksiyonu
const setFontSafe = (doc, fontName, fontStyle = 'normal') => {
    try {
        // Uyumlu adlandırma
        if (fontName === 'opensans-regular' || fontName === 'OpenSans' || fontName === 'OpenSans-Regular') {
            doc.setFont('OpenSans', fontStyle);
        } else {
            doc.setFont(fontName, fontStyle);
        }
    } catch (error) {
        console.warn(`Font ${fontName} not available, using helvetica`);
        doc.setFont('helvetica', fontStyle);
    }
};



// Türkçe karakterleri İngilizce karakterlere çevir
/** Sınıf listesi PDF: yüklemede çizgi; eksik/0 → 0.0; sayı doğrudan (önünde % yok). */
const formatRosterExamScoreForPdf = (score, scoresReady) => {
    if (!scoresReady) return '-';
    const n = score == null || score === '' ? NaN : Number(score);
    const v = Number.isFinite(n) ? n : 0;
    if (v <= 0) return '0.0';
    return v.toFixed(1);
};

const turkishToEnglish = (text) => {
    if (!text) return '';
    
    const turkishChars = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G',
        'ı': 'i', 'İ': 'I',
        'ö': 'o', 'Ö': 'O',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U'
    };
    
    return text.replace(/[çÇğĞıİöÖşŞüÜ]/g, letter => turkishChars[letter] || letter);
};

// Durum metnini Türkçe gösterim için dönüştür
const toTurkishStatus = (status) => {
    const norm = turkishToEnglish(String(status || '').trim().replace(/\s+/g, ' '));
    if (norm === 'Mukemmel') return 'Mükemmel';
    if (norm === 'Iyi') return 'İyi';
    if (norm === 'Orta') return 'Orta';
    if (norm === 'Zayif') return 'Zayıf';
    if (norm === 'Cok Zayif') return 'Çok Zayıf';
    return status || '';
};

// Güvenli dosya adı üretimi (Türkçe karakterleri korur, yasaklı karakterleri temizler)
const sanitizeFilename = (name) => {
    try {
        if (!name) return 'Rapor';
        // eslint-disable-next-line no-control-regex
        const illegal = /[<>:"/\\|?*\u0000-\u001F]/g;
        let s = String(name).normalize('NFC').replace(illegal, ' ');
        s = s.replace(/\s+/g, ' ').trim();
        s = s.replace(/\s/g, '_');
        s = s.replace(/_+/g, '_');
        return s || 'Rapor';
    } catch {
        return 'Rapor';
    }
};

// Basit harf notu hesaplama
const computeLetterGrade = (percent) => {
    const p = Number(percent || 0);
    if (p >= 90) return 'AA';
    if (p >= 85) return 'BA';
    if (p >= 80) return 'BB';
    if (p >= 75) return 'CB';
    if (p >= 60) return 'CC';
    if (p >= 55) return 'DC';
    if (p >= 50) return 'DD';
    if (p >= 40) return 'FD';
    return 'FF';
};

/** Değerlendirme sonuçları sayfasıyla aynı ağırlıklı genel yüzde (0–100). */
const computeWeightedGeneralForRoster = (rec, weightsByType) => {
    const r = rec || {};
    const wb = weightsByType || {};
    const hasBut = typeof r.but === 'number' && r.but > 0;
    let total = 0;
    const finalWeight = wb.final || 0;
    Object.keys(wb).forEach((t) => {
        const wt = wb[t] || 0;
        const pts = Number(r[t] != null ? r[t] : 0);
        if (!Number.isFinite(pts)) return;
        if (hasBut && t === 'final') return;
        if (hasBut && t === 'but') {
            if (finalWeight > 0) total += pts * (finalWeight / 100);
            return;
        }
        if (wt > 0) total += pts * (wt / 100);
    });
    return Number(total.toFixed(2));
};

// Basit tablo çizim fonksiyonu
const drawTable = (doc, data, headers, startY, options = {}) => {
    // Veri kontrolü
    if (!data || !Array.isArray(data) || data.length === 0) {
        return startY;
    }
    
    if (!headers || !Array.isArray(headers) || headers.length === 0) {
        return startY;
    }
    
    const {
        fontSize = 10,
        startX = 20,
        marginRight = 20,
        columnWidths = [],
        headerAlign = 'center',
        statusTextColors = false,
        cellHeight: initialCellHeight = 16,
        keepTurkish = false
    } = options;
    
    let currentY = startY;
    let cellHeight = initialCellHeight; // cellHeight değişkenini tanımla
    
    // Sayfa boyutları
    const pageWidth = doc.internal.pageSize.getWidth();
    const availableWidth = pageWidth - startX - marginRight;
    const pageHeight = doc.internal.pageSize.getHeight ? doc.internal.pageSize.getHeight() : 297;
    
    // Sütun genişliklerini hesapla
    let baseWidths = columnWidths;
    if (!baseWidths || baseWidths.length === 0) {
        baseWidths = headers.map(() => 30); // Varsayılan genişlik
    }
    
    // Toplam genişliği kontrol et ve gerekirse ölçekle
    const totalBaseWidth = baseWidths.reduce((sum, width) => sum + (width || 30), 0);
    let scale = 1;
    
    if (totalBaseWidth > availableWidth) {
        scale = availableWidth / totalBaseWidth;
    }
    
    const cellWidths = baseWidths.map(w => (w || 30) * scale);
    
    // Türkçe karakter desteği için gelişmiş font ayarları
    setFontSafe(doc, 'OpenSans');
    if (typeof doc.setLanguage === 'function') {
        try { doc.setLanguage('tr'); } catch {}
    }
    
    // Türkçe karakter kodlaması için özel ayarlar
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);
    
    // Başlık satırı - daha şık tasarım
    doc.setFontSize(fontSize + 1);
    setFontSafe(doc, 'OpenSans', 'bold');
    
    headers.forEach((header, index) => {
        const x = startX + cellWidths.slice(0, index).reduce((sum, width) => sum + width, 0);
        const width = cellWidths[index] || 30;
        
        // Başlık hücresi - KOSTÜ mavi rengi
        const headerColor = [2, 59, 88]; // KOSTÜ mavi rengi
        doc.setFillColor(headerColor[0], headerColor[1], headerColor[2]);
        doc.rect(x, currentY, width, cellHeight + 8, 'F');
        
        // Başlık hücresi çerçevesi - daha kalın
        doc.setDrawColor(1, 40, 70);
        doc.setLineWidth(0.8);
        doc.rect(x, currentY, width, cellHeight + 8, 'S');
        
        // Başlık metni - beyaz ve ortalanmış, daha büyük font
        doc.setFontSize(fontSize + 1);
        setFontSafe(doc, 'OpenSans', 'bold');
        doc.setTextColor(255, 255, 255);
        
        const headerText = header ? String(header) : '';
        
        // Başlık metnini tek satırda göster, daha büyük font
        if (headerAlign === 'center') {
            doc.text(headerText, x + width / 2, currentY + 12, { align: 'center' });
        } else {
            doc.text(headerText, x + 6, currentY + 12);
        }
    });
    
    currentY += cellHeight + 8;
    
    // Satır rengi belirleme - sadece durum hücresi için
    let rowColor = [248, 250, 252]; // Varsayılan açık gri
    
    // Veri satırlarını çiz
    data.forEach((row, rowIndex) => {
        // Satır veri kontrolü
        if (!row || !Array.isArray(row)) {
            return;
        }
        
        // Sayfa kontrolü
        if (currentY > pageHeight - 50) {
            doc.addPage();
            currentY = 20;
        }
        
        // Satır arka planı - sadece açık gri
        doc.setFillColor(rowColor[0], rowColor[1], rowColor[2]);
        doc.rect(startX, currentY, availableWidth, cellHeight, 'F');
        
        // Hücreleri çiz
        row.forEach((cell, cellIndex) => {
            const x = startX + cellWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
            const width = cellWidths[cellIndex] || 30;
            
            // Hücre çerçevesi
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.3);
            doc.rect(x, currentY, width, cellHeight, 'S');
            
            // Hücre metni
            doc.setFontSize(fontSize);
            setFontSafe(doc, 'OpenSans', 'normal');
            if (typeof doc.setLanguage === 'function') { try { doc.setLanguage('tr'); } catch {} }
            
            // Durum metni için özel renk - son sütun kontrolü
            const isLastColumn = cellIndex === row.length - 1;
            const isStatusColumn = statusTextColors && isLastColumn;
            
            if (isStatusColumn) {
                const rawStatus = String(cell).trim().replace(/\s+/g, ' ');
                const statusNorm = turkishToEnglish(rawStatus); // normalize for comparison
                // eslint-disable-next-line no-unused-vars
                const status = keepTurkish ? rawStatus : statusNorm;
                
                // Durum hücresi için özel font ayarları - daha küçük ve kalın
                doc.setFontSize(fontSize - 1); // Bir puan daha küçük font
                setFontSafe(doc, 'OpenSans', 'bold'); // Kalın font
                if (typeof doc.setLanguage === 'function') { try { doc.setLanguage('tr'); } catch {} }
                
                // Durum hücresi için özel arka plan rengi - düzeltilmiş Türkçe karakterler
                // Yeni durum etiketleri için renk haritası
                if (statusNorm.includes('Iyilestirilmeli')) {
                    doc.setFillColor(254, 226, 226); // kırmızımsı arka plan
                    doc.setTextColor(185, 28, 28);
                } else if (statusNorm.includes('Gelistiriliyor')) {
                    doc.setFillColor(254, 249, 195); // sarı arka plan
                    doc.setTextColor(245, 158, 11);
                } else if (statusNorm.includes('Yeterli')) {
                    doc.setFillColor(220, 252, 231); // yeşil arka plan
                    doc.setTextColor(21, 128, 61);
                } else if (statusNorm.includes('Ortalamanin Uzerinde')) {
                    doc.setFillColor(219, 234, 254); // mavi arka plan
                    doc.setTextColor(59, 130, 246);
                } else if (statusNorm.includes('Mukemmel')) {
                    doc.setFillColor(220, 252, 231); // Açık yeşil arka plan
                    doc.setTextColor(21, 128, 61); // Koyu yeşil metin
                } else if (statusNorm.includes('Iyi')) {
                    doc.setFillColor(219, 234, 254); // Açık mavi arka plan
                    doc.setTextColor(59, 130, 246); // Mavi metin
                } else if (statusNorm.includes('Orta')) {
                    doc.setFillColor(254, 249, 195); // Açık sarı arka plan
                    doc.setTextColor(245, 158, 11); // Turuncu metin
                } else if (statusNorm.includes('Zayif')) {
                    doc.setFillColor(254, 242, 242); // Açık kırmızı arka plan
                    doc.setTextColor(239, 68, 68); // Kırmızı metin
                } else if (statusNorm.includes('Cok Zayif')) {
                    doc.setFillColor(254, 226, 226); // Daha açık kırmızı arka plan
                    doc.setTextColor(185, 28, 28); // Koyu kırmızı metin
                } else {
                    doc.setFillColor(248, 250, 252); // Varsayılan arka plan
                    doc.setTextColor(55, 65, 81); // Varsayılan metin rengi
                }
                
                // Durum hücresi arka planını çiz
                doc.rect(x, currentY, width, cellHeight, 'F');
            } else {
                // Normal hücreler için standart font ayarları
                doc.setFontSize(fontSize);
                setFontSafe(doc, 'OpenSans', 'normal');
                if (typeof doc.setLanguage === 'function') { try { doc.setLanguage('tr'); } catch {} }
                doc.setTextColor(55, 65, 81);
            }
            
            const cellText = cell ? String(cell).trim().replace(/\s+/g, ' ') : ''; // Sadece boşlukları temizle
            
            // Metin sığdırma - uzun metinler için çok satırlı gösterim
            const maxWidth = width - 6;
            const textWidth = doc.getTextWidth(cellText);
            
            if (textWidth > maxWidth && cellText.length > 20) {
                // Uzun metinler için çok satırlı gösterim
                const words = cellText.split(' ');
                const lines = [];
                let currentLine = '';
                
                words.forEach(word => {
                    const testLine = currentLine + (currentLine ? ' ' : '') + word;
                    const testWidth = doc.getTextWidth(testLine);
                    
                    if (testWidth <= maxWidth) {
                        currentLine = testLine;
                    } else {
                        if (currentLine) {
                            lines.push(currentLine);
                        }
                        currentLine = word;
                    }
                });
                
                if (currentLine) {
                    lines.push(currentLine);
                }
                
                // Çok satırlı metni yaz - daha fazla satır
                lines.forEach((line, lineIndex) => {
                    if (lineIndex < 4) { // Maksimum 4 satır
                        const lineY = currentY + (lineIndex * (fontSize + 1)) + 6;
                        doc.text(line, x + 3, lineY);
                    } else if (lineIndex === 4) {
                        // 5. satır varsa "..." ekle
                        const lastLine = lines[3] + '...';
                        const lastLineY = currentY + (3 * (fontSize + 1)) + 6;
                        doc.text(lastLine, x + 3, lastLineY);
                    }
                });
                
                // Hücre yüksekliğini artır
                const extraHeight = Math.min(lines.length - 1, 3) * (fontSize + 1);
                const newCellHeight = Math.max(cellHeight, 16 + extraHeight);
                
                // Hücre yüksekliğini güncelle
                cellHeight = newCellHeight;
            } else {
                // Kısa metinler için normal gösterim - durum hücreleri için özel hizalama
                const textX = x + width / 2;
                const textY = currentY + cellHeight / 2 + 3;
                
                if (isStatusColumn) {
                    // Durum hücreleri için daha kompakt hizalama - tam merkez
                    const statusTextX = x + width / 2;
                    const statusTextY = currentY + cellHeight / 2 + 2; // Biraz yukarı
                    doc.text(cellText, statusTextX, statusTextY, { align: 'center' });
                } else {
                    // Normal hücreler için standart hizalama
                    doc.text(cellText, textX, textY, { align: 'center' });
                }
            }
        });
        
        currentY += cellHeight;
    });
    
    return currentY;
};

const PDFExporter = ({ 
    filteredResults, 
    selectedProgramId, 
    selectedCourseId, 
    selectedExamTypeForAnalysis,
    questions = [],
    studentList = [],
    learningOutcomes = [],
    courseOutcomes = [],
    programOutcomes = [],
    programs = [],
    courses = [],
    weights = [],
    classRoster = [],
    examTypeOrder = [],
    examTypeLabels = {},
    weightsByType = {},
    studentScoresByKey = {},
    examPercentsByStudent = {},
    scoresReady = false,
    examAttempts = [],
    generalRates = null
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [fontReady, setFontReady] = useState(false);
    const [pdfExamFilter, setPdfExamFilter] = useState(selectedExamTypeForAnalysis || '');
    const [showOutcomePicker, setShowOutcomePicker] = useState(false);
    const [availableLOs, setAvailableLOs] = useState([]);
    const [selectedLOIds, setSelectedLOIds] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState('');
    const [availableStudents, setAvailableStudents] = useState([]);

    const [exportOptions, setExportOptions] = useState({
        includeStudentList: true,
        includeLearningOutcomes: true,
        includeProgramOutcomes: true,
        includeStudentAnalysis: false
    });

    // Seçili isimleri al
    const getSelectedNames = async () => {
        try {
            let programName = 'N/A';
            let courseName = 'N/A';
            
            // Önce filteredResults'tan al
            if (filteredResults) {
                if (filteredResults.program_name) {
                    programName = filteredResults.program_name;
                }
                // Rektör/Dekan sayfalarında course_info içinde gelebilir
                const courseInfoName = filteredResults.course_info?.course_name || filteredResults.course_name;
                if (courseInfoName) {
                    courseName = courseInfoName;
                }
            }

            // Program/ders listesi props'larından bul
            if (programName === 'N/A') {
                const pid = selectedProgramId || filteredResults?.program_id;
                const foundProgram = programs.find(p => String(p.id) === String(pid));
                if (foundProgram?.name) {
                    programName = foundProgram.name;
                }
            }
            if (courseName === 'N/A') {
                const cid = selectedCourseId || filteredResults?.course_id || filteredResults?.course_info?.course_id;
                const foundCourse = courses.find(c => String(c.id) === String(cid));
                if (foundCourse?.name || foundCourse?.course_name) {
                    courseName = foundCourse.name || foundCourse.course_name;
                }
            }
            
            // Eğer hala N/A ise API'den al - daha agresif yaklaşım
            if (programName === 'N/A') {
                const programIdToTry = selectedProgramId || (filteredResults && filteredResults.program_id);
                if (programIdToTry) {
                    try {
                        const programResponse = await axios.get(API_ENDPOINTS.PROGRAMS);
                        
                        const program = programResponse.data.find(p => p.id === programIdToTry);
                        if (program) {
                            programName = program.name;
                        } else {
                            // İlk programı al
                            if (programResponse.data.length > 0) {
                                programName = programResponse.data[0].name;
                            }
                        }
                    } catch (error) {
                        // Hata sessizce yoksayılıyor
                    }
                }
            }
            
            if (courseName === 'N/A') {
                const courseIdToTry = selectedCourseId || (filteredResults && filteredResults.course_id);
                if (courseIdToTry) {
                    try {
                        const courseResponse = await axios.get(`${API_BASE_URL}/api/courses`);
                        
                        const course = courseResponse.data.find(c => c.id === courseIdToTry);
                        if (course) {
                            courseName = course.name;
                        } else {
                            // İlk dersi al
                            if (courseResponse.data.length > 0) {
                                courseName = courseResponse.data[0].name;
                            }
                        }
                    } catch (error) {
                        // Hata sessizce yoksayılıyor
                    }
                }
            }

            // Son kontrol - varsayılan değerler
            if (programName === 'N/A') {
                programName = 'Program adı bulunamadı';
            }
            if (courseName === 'N/A') {
                courseName = 'Ders adı bulunamadı';
            }

            return {
                program: programName,
                course: courseName
            };
        } catch (error) {
            return { 
                program: 'Program adı bulunamadı', 
                course: 'Ders adı bulunamadı' 
            };
        }
    };

    // Yardımcı: sınav türünü normalize et
    const normalizeExamType = (v) => (v == null ? '' : String(v).trim().toLowerCase());

    // Mevcut Dersin öğrenme çıktıları listesini hazırla (modal için) - useMemo ile hesapla
    const availableLOsComputed = React.useMemo(() => {
        try {
            // Outcome kaynaklarını normalize et
            const fromProps = Array.isArray(learningOutcomes) ? learningOutcomes : [];
            const fromFiltered = Array.isArray(filteredResults?.learning_outcomes)
                ? filteredResults.learning_outcomes
                : [];
            const merged = [...fromProps, ...fromFiltered];
            // Normalize to {id, number, name}
            const normalized = merged.map((o, idx) => ({
                id: o.id != null ? o.id : (o.learning_outcome_id != null ? o.learning_outcome_id : idx + 1),
                number: o.number != null ? o.number : (o.code != null ? o.code : (o.codeNum || (o.code && String(o.code).replace(/[^0-9]/g, '')))),
                name: o.description || o.name || ''
            }));
            // Unique by id
            const uniqMap = new Map();
            normalized.forEach(n => {
                if (!uniqMap.has(n.id)) uniqMap.set(n.id, n);
            });
            const list = Array.from(uniqMap.values()).sort((a,b) => parseInt(a.number||0) - parseInt(b.number||0));
            return list;
        } catch {
            return [];
        }
    }, [learningOutcomes, filteredResults?.learning_outcomes]);
    
    // availableLOs state'ini güncelle - sadece değer değiştiğinde
    const availableLOsKey = JSON.stringify(availableLOsComputed.map(l => l.id));
    useEffect(() => {
        setAvailableLOs(availableLOsComputed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableLOsKey]);

    // Mevcut öğrenci listesini hazırla - useMemo ile hesapla
    const resultStudents = filteredResults?.students;
    const availableStudentsComputed = React.useMemo(() => {
        try {
            let students = [];
            
            if (Array.isArray(resultStudents)) {
                students = resultStudents;
            } else if (studentList && studentList.length > 0) {
                students = studentList;
            }

            const studentOptions = students.map((student, index) => ({
                id: student.student_no || student.student_number || student.no || index,
                name: student.student_name || student.name || student.full_name || student.ad_soyad || `Öğrenci ${index + 1}`,
                number: student.student_no || student.student_number || student.no || index
            }));

            return studentOptions;
        } catch (error) {
            return [];
        }
    }, [resultStudents, studentList]);
    
    // availableStudents state'ini güncelle - sadece değer değiştiğinde
    const availableStudentsKey = JSON.stringify(availableStudentsComputed.map(s => s.id));
    useEffect(() => {
        setAvailableStudents(availableStudentsComputed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableStudentsKey]);



    // Öğrenci tablosu verilerini hazırla - Daha temiz ve düzenli
    const prepareStudentTableData = (isGeneral = false, noTransliterate = false) => {
        // Farklı veri kaynaklarını kontrol et
        let students = [];
        
        if (filteredResults && filteredResults.students) {
            students = filteredResults.students;
        } else if (studentList && studentList.length > 0) {
            students = studentList;
        } else {
            return [];
        }

        // Tekrarlanan öğrencileri filtrele - öğrenci numarasına göre unique yap
        const uniqueStudents = [];
        const seenStudentIds = new Set();
        
        students.forEach(student => {
            const studentId = student.student_no || student.student_number || student.no || student.id;
            if (studentId && !seenStudentIds.has(String(studentId))) {
                seenStudentIds.add(String(studentId));
                uniqueStudents.push(student);
            }
        });
        
        // PDF'e özel filtreleme
        let filteredStudents = uniqueStudents;
        
        // Öğrenci özel analiz seçiliyse
        if (exportOptions.includeStudentAnalysis && selectedStudent) {
            filteredStudents = uniqueStudents.filter((s) => {
                const studentId = s.student_no || s.student_number || s.no || s.id;
                return String(studentId) === String(selectedStudent);
            });
        } 
        // Normal sınav türü filtresi
        else if (pdfExamFilter) {
            const selNorm = normalizeExamType(pdfExamFilter);
            filteredStudents = uniqueStudents.filter((s) => normalizeExamType(s.exam_type || s.examType) === selNorm);
        }

        return filteredStudents.map((student, index) => {
            // Öğrenci bilgilerini al - daha güvenli
            const studentNo = student.student_no || student.student_number || student.no || student.number || `Ogrenci ${index + 1}`;
            const studentName = student.student_name || student.name || student.full_name || student.ad_soyad || 'Isimsiz';
            const examType = student.exam_type || student.examType || pdfExamFilter || 'N/A';
            
            // Cevap anahtarını al ve array'e çevir
            let studentAnswers = [];
            if (student.answer_key) {
                if (typeof student.answer_key === 'string') {
                    try {
                        studentAnswers = JSON.parse(student.answer_key);
                    } catch (e) {
                        studentAnswers = student.answer_key.split(',').map(ans => ans.trim());
                    }
                } else if (Array.isArray(student.answer_key)) {
                    studentAnswers = student.answer_key;
                }
            } else if (student.answers) {
                // Alternatif alan adı
                if (Array.isArray(student.answers)) {
                    studentAnswers = student.answers;
                }
            }
            
            // Eğer cevap anahtarı yoksa, varsayılan değerler
            const totalQuestions = studentAnswers.length || questions.length || 0;
            
            // Doğru cevapları say (büyük harfler: A, B, C, D, E)
            const correctAnswers = studentAnswers.filter(ans => 
                ans && typeof ans === 'string' && ans.length === 1 && 
                ans.toUpperCase() === ans && ans !== ans.toLowerCase() &&
                ['A', 'B', 'C', 'D', 'E'].includes(ans.toUpperCase())
            ).length;
            
            // Yanlış cevapları say (küçük harfler: a, b, c, d, e)
            const wrongAnswers = studentAnswers.filter(ans => 
                ans && typeof ans === 'string' && ans.length === 1 && 
                ans.toLowerCase() === ans && ans !== ans.toUpperCase() &&
                ['a', 'b', 'c', 'd', 'e'].includes(ans.toLowerCase())
            ).length;
            
            const emptyAnswers = Math.max(0, totalQuestions - correctAnswers - wrongAnswers);
            const successRate = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
            
            // Durum belirleme - Kısa ve net, boşluk olmadan
            let status = 'Dusuk';
            if (successRate >= 85) status = 'Mukemmel';
            else if (successRate >= 70) status = 'Iyi';
            else if (successRate >= 50) status = 'Orta';
            else if (successRate >= 30) status = 'Zayif';
            else status = 'Cok Zayif';
            
            // Status metnini temizle ve normalize et - Türkçe karakterleri koru
            // eslint-disable-next-line no-unused-vars
            status = status.trim().replace(/\s+/g, ' '); // Sadece boşlukları temizle
            
            if (isGeneral) {
                // Genel görünüm – sadece Genel puan ve harf notu (Durum sütunu yok)
                const letter = computeLetterGrade(successRate);
                return [
                    String(studentNo).substring(0, 10),
                    (noTransliterate ? (studentName || '') : turkishToEnglish(studentName)).substring(0, 20),
                    `${successRate.toFixed(0)}`,
                    letter
                ];
            }
            // Detaylı görünüm – Durum sütunu kaldırıldı
            return [
                String(studentNo).substring(0, 10), // Öğrenci no
                (noTransliterate ? (studentName || '') : turkishToEnglish(studentName)).substring(0, 20), // Ad soyad
                noTransliterate ? (examType || '') : turkishToEnglish(examType),
                totalQuestions || 0,
                correctAnswers || 0,
                wrongAnswers || 0,
                emptyAnswers || 0,
                `${successRate.toFixed(1)}%`
            ];
        });
    };

    // Sayfadaki sınıf listesi tablosuyla birebir veri hazırla
    const prepareClassRosterTableData = () => {
        if (!Array.isArray(classRoster) || classRoster.length === 0) {
            return { headers: [], rows: [] };
        }

        const normalizedExamOrder = (Array.isArray(examTypeOrder) && examTypeOrder.length > 0)
            ? examTypeOrder
            : Array.from(new Set((weights || []).map(w => normalizeExamType(w.type || w.exam_type)).filter(Boolean)));

        const labelForType = (t) => {
            const direct = examTypeLabels?.[t];
            if (direct) return direct;
            const found = (weights || []).find(w => normalizeExamType(w.type || w.exam_type) === t);
            return (found?.type || found?.exam_type || t || '').toString();
        };

        const headers = [
            '#',
            'Öğrenci No',
            'Adı Soyadı',
            ...normalizedExamOrder.map(t => {
                const wt = weightsByType?.[t];
                return `${labelForType(t)} (${wt != null ? `%${wt}` : '-'})`;
            }),
            'Harf',
            'Öntest'
        ];

        const rows = classRoster.map((student, idx) => {
            const rosterNo = String(student.student_no || '').trim();
            const rosterNameKey = String(student.student_name || '').trim().toLowerCase();
            const key = rosterNo || rosterNameKey;
            const rec = scoresReady ? (studentScoresByKey?.[key] || examPercentsByStudent?.[key] || {}) : {};
            const studentAttempt = (examAttempts || []).find(a => String(a.student_no || '') === rosterNo);
            const pretestStatus = studentAttempt
                ? (studentAttempt.status === 'Submitted' ? '✓' : studentAttempt.status === 'InProgress' ? '⏳' : String(studentAttempt.status || '-'))
                : '-';
            const genelPct = scoresReady ? computeWeightedGeneralForRoster(rec, weightsByType) : NaN;
            const harfStr = scoresReady && Number.isFinite(genelPct) ? computeLetterGrade(genelPct) : '-';

            return [
                String(idx + 1),
                rosterNo,
                fontReady ? (student.student_name || '') : turkishToEnglish(student.student_name || ''),
                ...normalizedExamOrder.map((t) => {
                    const score = rec?.[t];
                    return formatRosterExamScoreForPdf(score, scoresReady);
                }),
                harfStr,
                pretestStatus
            ];
        });

        return { headers, rows };
    };

    // Öğrenci sayısını hesapla - sınıf listesi varsa onu kullan
    const studentCount = React.useMemo(() => {
        if (classRoster && classRoster.length > 0) return classRoster.length;
        const isGeneral = !pdfExamFilter || pdfExamFilter === '';
        const studentData = prepareStudentTableData(isGeneral);
        return studentData.length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classRoster?.length, pdfExamFilter, filteredResults?.students?.length, studentList?.length, exportOptions.includeStudentAnalysis, selectedStudent, questions?.length]);

    // Render sırasında gösterilecek ÖÇ ve PÇ sayıları (cache'lenmiş)
    const learningOutcomesCount = React.useMemo(() => {
        try {
            const loList = (Array.isArray(learningOutcomes) && learningOutcomes.length)
                ? learningOutcomes
                : ((Array.isArray(courseOutcomes) && courseOutcomes.length)
                    ? courseOutcomes
                    : (Array.isArray(filteredResults?.learning_outcomes) ? filteredResults.learning_outcomes : []));
            return loList.length;
        } catch {
            return 0;
        }
    }, [learningOutcomes, courseOutcomes, filteredResults?.learning_outcomes]);

    const programOutcomesCount = React.useMemo(() => {
        try {
            const poList = Array.isArray(programOutcomes) && programOutcomes.length
                ? programOutcomes
                : (Array.isArray(filteredResults?.program_outcomes) ? filteredResults.program_outcomes : []);
            return poList.length;
        } catch {
            return 0;
        }
    }, [programOutcomes, filteredResults?.program_outcomes]);

    // Dersin öğrenme çıktıları verilerini hazırla (ÖÇ) – sayfadaki hesapla aynı mantık
    const prepareLearningOutcomesData = (currentSelectedLOIds = null, noTransliterate = false) => {
        try {
            let students = Array.isArray(filteredResults?.students) ? filteredResults.students : [];
            // Sınav türü filtresi uygulanır (Genel değilse)
            if (pdfExamFilter) {
                const selNorm = normalizeExamType(pdfExamFilter);
                students = students.filter(st => normalizeExamType(st.exam_type || st.examType) === selNorm);
            }
            const loList = (Array.isArray(learningOutcomes) && learningOutcomes.length)
                ? learningOutcomes
                : ((Array.isArray(courseOutcomes) && courseOutcomes.length)
                    ? courseOutcomes
                    : (Array.isArray(filteredResults?.learning_outcomes) ? filteredResults.learning_outcomes : []));

            // Seçim filtresi
            const selectedIdsToUse = currentSelectedLOIds !== null ? currentSelectedLOIds : selectedLOIds;
            const selectedSet = new Set(selectedIdsToUse || []);
            const filteredLOs = (selectedSet.size > 0)
                ? loList.filter(o => {
                    const oid = o.id != null ? o.id : (o.learning_outcome_id != null ? o.learning_outcome_id : o.code);
                    return selectedSet.has(oid);
                })
                : loList;

            // Sorular: ÖÇ eşleşmesi topics alanından
            const qs = Array.isArray(questions) ? questions : [];

            const rows = filteredLOs.map((outcome) => {
                const outcomeId = outcome.id;
                const relatedIdxs = qs
                    .map((q, idx) => (q?.topics && q.topics.map(String).includes(String(outcomeId)) ? idx : -1))
                    .filter(idx => idx !== -1);

                // Tüm öğrencilerde başarı oranı ortalaması
                const perStudentRates = students.map(st => {
                    const answers = st.answer_key || [];
                    const relatedAnswers = relatedIdxs.map(i => answers[i]);
                    const correct = relatedAnswers.filter(a => a && a === a.toUpperCase() && a !== a.toLowerCase()).length;
                    const total = relatedAnswers.length;
                    return total > 0 ? (correct / total) * 100 : 0;
                });

                const avg = students.length > 0
                    ? Number((perStudentRates.reduce((s, x) => s + x, 0) / students.length).toFixed(2))
                    : 0;

                // ÖÇ PDF durumu: sayfadaki eşiklerle aynı
                let status;
                if (avg < 50) status = 'İyileştirilmeli (1)';
                else if (avg < 60) status = 'Geliştiriliyor (2)';
                else if (avg < 75) status = 'Yeterli (3)';
                else status = 'Ortalamanın Üzerinde (4)';

                const codeText = outcome.code || `ÖÇ${outcomeId}`;
                return [
                    noTransliterate ? String(codeText) : turkishToEnglish(String(codeText)),
                    noTransliterate ? (outcome.description || outcome.name || 'N/A') : turkishToEnglish(outcome.description || outcome.name || 'N/A'),
                    `${avg.toFixed(2)}%`,
                    noTransliterate ? toTurkishStatus(status).trim().replace(/\s+/g, ' ') : turkishToEnglish(status.trim().replace(/\s+/g, ' '))
                ];
            });

            return rows;
        } catch (e) {
            return [];
        }
    };

    // Program çıktıları verilerini hazırla (PÇ) – sayfadaki hesapla aynı mantık
    const prepareProgramOutcomesData = (noTransliterate = false) => {
        try {
            let students = Array.isArray(filteredResults?.students) ? filteredResults.students : [];
            // Sınav türü filtresi uygulanır (Genel değilse)
            if (pdfExamFilter) {
                const selNorm = normalizeExamType(pdfExamFilter);
                students = students.filter(st => normalizeExamType(st.exam_type || st.examType) === selNorm);
            }
            const poList = Array.isArray(programOutcomes) && programOutcomes.length
                ? programOutcomes
                : (Array.isArray(filteredResults?.program_outcomes) ? filteredResults.program_outcomes : []);

            const qs = Array.isArray(questions) ? questions : [];

            const rows = poList.map((outcome) => {
                const outcomeId = outcome.id;
                const relatedIdxs = qs
                    .map((q, idx) => (q?.program_outcomes && q.program_outcomes.map(String).includes(String(outcomeId)) ? idx : -1))
                    .filter(idx => idx !== -1);

                const perStudentRates = students.map(st => {
                    const answers = st.answer_key || [];
                    const relatedAnswers = relatedIdxs.map(i => answers[i]);
                    const correct = relatedAnswers.filter(a => a && a === a.toUpperCase() && a !== a.toLowerCase()).length;
                    const total = relatedAnswers.length;
                    return total > 0 ? (correct / total) * 100 : 0;
                });

                const avg = students.length > 0
                    ? Number((perStudentRates.reduce((s, x) => s + x, 0) / students.length).toFixed(2))
                    : 0;

                // PÇ PDF durumu: sayfadaki eşiklerle aynı
                let status;
                if (avg < 50) status = 'İyileştirilmeli (1)';
                else if (avg < 60) status = 'Geliştiriliyor (2)';
                else if (avg < 75) status = 'Yeterli (3)';
                else status = 'Ortalamanın Üzerinde (4)';

                const codeText = outcome.code || `PÇ${outcomeId}`;
                return [
                    noTransliterate ? String(codeText) : turkishToEnglish(String(codeText)),
                    noTransliterate ? (outcome.description || outcome.name || 'N/A') : turkishToEnglish(outcome.description || outcome.name || 'N/A'),
                    `${avg.toFixed(2)}%`,
                    noTransliterate ? status.trim().replace(/\s+/g, ' ') : turkishToEnglish(status.trim().replace(/\s+/g, ' '))
                ];
            });

            return rows;
        } catch (e) {
            return [];
        }
    };



    // PDF oluştur: önizleme veya indir
    const generatePDF = async (asPreview = false) => {
        // Eğer ders Dersin Öğrenme Çıktısı seçilmemişse, mevcut tüm çıktıları otomatik seç
        // Sınav sonuçları sayfasında modal göstermek yerine tüm çıktıları dahil et
        let currentSelectedLOIds = selectedLOIds;
        if (!currentSelectedLOIds || currentSelectedLOIds.length === 0) {
            if (availableLOs && availableLOs.length > 0) {
                // Tüm mevcut Dersin öğrenme çıktılarını otomatik olarak seç
                const allLOIds = availableLOs.map(lo => lo.id);
                setSelectedLOIds(allLOIds);
                currentSelectedLOIds = allLOIds; // Hemen kullanmak için yerel değişkene ata
            } else {
                // Modal açmak yerine devam et - sınav sonuçları için modal gereksiz
                currentSelectedLOIds = []; // Boş array ile devam et
            }
        }

        // Öğrenci özel analiz kontrolü
        if (exportOptions.includeStudentAnalysis && !selectedStudent) {
            alert('Lütfen öğrenci özel analiz için bir öğrenci seçin.');
            return;
        }

        // Veri kontrolü
        const studentData = prepareStudentTableData(false, fontReady);
        const learningData = prepareLearningOutcomesData(currentSelectedLOIds, fontReady);
        const programData = prepareProgramOutcomesData(fontReady);

        if (studentData.length === 0 && learningData.length === 0 && programData.length === 0) {
            alert('PDF oluşturmak için önce sınav sonuçlarını yükleyin veya veri seçin.');
            return;
        }

        try {
            asPreview ? setIsPreviewing(true) : setIsGenerating(true);

            const doc = new jsPDF('p', 'mm', 'a4');
            
            // OpenSans fontunu yükle
            const fontOk = await loadOpenSansFont(doc);
            setFontReady(!!fontOk);
            setFontSafe(doc, fontOk ? 'OpenSans' : 'helvetica');
            
            // Türkçe karakter desteği için gelişmiş font ayarları
            setFontSafe(doc, 'OpenSans');
            doc.setLanguage('tr');
            
            // Türkçe karakter desteği için ek ayarlar
            doc.setFontSize(12);
            doc.setTextColor(0, 0, 0);
            
            // Türkçe karakter kodlaması için özel ayarlar
            doc.setProperties({
                title: 'Sınav Sonuçları Raporu',
                subject: 'Öğrenci Başarı Analizi',
                author: 'KOSTÜ',
                creator: 'KOSTÜ Öğrenci Başarı Analizi Sistemi',
                language: 'tr-TR'
            });
            
            const selectedNames = await getSelectedNames();
            
            // Kenar boşlukları ve yardımcı çizgi fonksiyonu
            const pageWidth = doc.internal.pageSize.getWidth();
            const leftMargin = 15;
            const rightMargin = 15;
            const contentStartX = leftMargin;
            const contentEndX = pageWidth - rightMargin;
            const hr = (y, width = 0.5) => {
                doc.setDrawColor(30, 41, 59);
                doc.setLineWidth(width);
                doc.line(contentStartX, y, contentEndX, y);
            };
            
            // İlk sayfa kapağı - Şık tasarım
            doc.setFontSize(28);
            setFontSafe(doc, 'OpenSans', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('SINAV SONUÇLARI RAPORU', 105, 35, { align: 'center' });
            
            // Dekoratif çerçeve
            doc.setDrawColor(2, 59, 88);
            doc.setLineWidth(1);
            doc.rect(20, 20, 170, 250);
            
            // Logo ekle
            try {
                doc.addImage('/KOSTU-Yuvarlak-Logo.png', 'PNG', 85, 45, 40, 40);
            } catch (e) {
                // Logo yüklenemedi, devam ediliyor
            }
            
            // Oluşturulma tarihi - Basit ve düzgün
            doc.setFontSize(12);
            setFontSafe(doc, 'OpenSans', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('Tarih:', 40, 100);
            
            doc.setFontSize(12);
            setFontSafe(doc, 'OpenSans', 'normal');
            doc.setTextColor(55, 65, 81);
            // Basit tarih formatı
            const today = new Date();
            const day = String(today.getDate()).padStart(2, '0');
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const year = today.getFullYear();
            const simpleDate = `${day}.${month}.${year}`;
            doc.text(simpleDate, 95, 100);
            
            // Sınav bilgileri başlığı
            doc.setFontSize(18);
            setFontSafe(doc, 'OpenSans', 'bold');
            doc.setTextColor(30, 41, 59);
            doc.text('SINAV BİLGİLERİ', 105, 125, { align: 'center' });
            
            // Ayırıcı çizgi - daha şık
            doc.setDrawColor(2, 59, 88);
            doc.setLineWidth(2);
            doc.line(40, 135, 170, 135);
            
            // Sınav detayları - daha düzenli
            doc.setFontSize(12);
            setFontSafe(doc, 'OpenSans', 'normal');
            doc.setTextColor(55, 65, 81);
            
            let courseDisplayName = selectedNames.course;
            let programDisplayName = selectedNames.program;
            
            // Eğer hala N/A ise filteredResults'tan al - daha agresif
            if (courseDisplayName === 'N/A' && filteredResults) {
                if (filteredResults.course_name) {
                    courseDisplayName = filteredResults.course_name;
                } else if (filteredResults.course) {
                    courseDisplayName = filteredResults.course;
                } else if (filteredResults.courseName) {
                    courseDisplayName = filteredResults.courseName;
                }
            }
            if (programDisplayName === 'N/A' && filteredResults) {
                if (filteredResults.program_name) {
                    programDisplayName = filteredResults.program_name;
                } else if (filteredResults.program) {
                    programDisplayName = filteredResults.program;
                } else if (filteredResults.programName) {
                    programDisplayName = filteredResults.programName;
                }
            }
            
            // Eğer hala N/A ise varsayılan değerler
            if (programDisplayName === 'N/A') {
                programDisplayName = 'AĞIZ VE DİŞ SAĞLIĞI PR.';
            }
            if (courseDisplayName === 'N/A') {
                courseDisplayName = 'MESLEKİ TERMİNOLOJİ';
            }
            
            const infoRows = [
                { label: 'Program', value: programDisplayName },
                { label: 'Ders', value: courseDisplayName },
                { label: 'Sınav Türü', value: pdfExamFilter || 'Tümü' },
                { label: 'Toplam Öğrenci', value: String(studentData.length || 0) }
            ];

            let infoY = 150;
            infoRows.forEach(row => {
                // Label
                doc.setFontSize(12);
                setFontSafe(doc, 'OpenSans', 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text(`${row.label}:`, 40, infoY);

                // Value
                const hasValue = row.value && String(row.value).trim() !== '' && row.value !== 'N/A';
                let valueText = hasValue ? String(row.value) : 'Veri Yok';
                
                // Türkçe karakter düzeltmesi
                if (!fontReady) valueText = turkishToEnglish(valueText);

                if (hasValue) {
                    setFontSafe(doc, 'OpenSans', 'normal');
                    doc.setTextColor(55, 65, 81);
                } else {
                    setFontSafe(doc, 'OpenSans', 'normal');
                    doc.setTextColor(156, 163, 175);
                }

                doc.text(valueText, 90, infoY);
                infoY += 20; // Daha fazla boşluk
            });
            
            // Alt bilgi
            doc.setFontSize(10);
            // Normalize italic to normal for OpenSans (single weight embedded)
            setFontSafe(doc, 'OpenSans', 'normal');
            doc.setTextColor(156, 163, 175); // Daha açık renk
            doc.text('KOSTÜ Öğrenci Başarı Analizi Sistemi', 105, 250, { align: 'center' });
            doc.text('Bu rapor otomatik olarak oluşturulmuştur.', 105, 255, { align: 'center' });
            
            let currentY = 180;
            
            // Öğrenci listesi tablosu (sayfadaki sınıf listesi öncelikli)
            const classRosterTable = prepareClassRosterTableData();
            const hasClassRosterTable = classRosterTable.rows.length > 0;
            if (exportOptions.includeStudentList && (studentData.length > 0 || hasClassRosterTable)) {
                doc.addPage();
                doc.setFontSize(18);
                setFontSafe(doc, 'OpenSans', 'bold');
                doc.setTextColor(30, 41, 59);
                doc.text('ÖĞRENCİ LİSTESİ', 105, 25, { align: 'center' });
                
                hr(30);
                
                // Özet bilgiler
                doc.setFontSize(10);
                setFontSafe(doc, 'OpenSans', 'normal');
                doc.setTextColor(107, 114, 128);
                doc.text(`Toplam Öğrenci: ${hasClassRosterTable ? classRosterTable.rows.length : studentData.length}`, leftMargin + 5, 40);
                doc.text(`Sınav Türü: ${fontReady ? (pdfExamFilter || 'Tümü') : turkishToEnglish(pdfExamFilter || 'Tümü')}`, contentEndX - 60, 40);

                let studentHeaders = [];
                let studentRows = [];
                let studentColumnWidths = [];

                if (hasClassRosterTable) {
                    studentHeaders = classRosterTable.headers;
                    studentRows = classRosterTable.rows;
                    const examColCount = Math.max(0, studentHeaders.length - 5);
                    studentColumnWidths = [
                        12, // #
                        28, // öğrenci no
                        46, // adı soyadı
                        ...Array(examColCount).fill(18),
                        12, // Harf
                        12 // Öntest
                    ];
                } else {
                    const isGeneral = !pdfExamFilter || pdfExamFilter === '';
                    studentHeaders = isGeneral
                        ? ['Öğrenci No', 'Ad Soyad', 'Genel', 'Harf Notu']
                        : ['Öğrenci No', 'Ad Soyad', 'Sınav Türü', 'Toplam', 'Doğru', 'Yanlış', 'Boş', 'Başarı Oranı'];
                    studentRows = prepareStudentTableData(isGeneral, fontReady);
                    studentColumnWidths = isGeneral
                        ? [35, 80, 30, 30]
                        : [30, 60, 25, 18, 20, 20, 18, 28];
                }

                currentY = drawTable(doc, studentRows, studentHeaders, 50, {
                    fontSize: 8,
                    columnWidths: studentColumnWidths,
                    startX: 15,
                    marginRight: 15,
                    rowColorMode: 'normal',
                    headerAlign: 'center',
                    statusTextColors: false,
                    cellHeight: 16,
                    keepTurkish: fontReady
                });


            }
            
            // Dersin öğrenme çıktıları Analizi Tablosu (sayfadaki generalRates öncelikli)
            const learningRowsForPdf = (Array.isArray(generalRates?.learningRates) && generalRates.learningRates.length > 0)
                ? generalRates.learningRates.map((outcome) => {
                    const avg = Number(outcome?.averageRate || 0);
                    let status = 'İyileştirilmeli (1)';
                    if (avg >= 75) status = 'Ortalamanın Üzerinde (4)';
                    else if (avg >= 60) status = 'Yeterli (3)';
                    else if (avg >= 50) status = 'Geliştiriliyor (2)';
                    return [
                        String(outcome?.code || `ÖÇ${outcome?.id || ''}`),
                        String(outcome?.description || ''),
                        `${avg.toFixed(2)}%`,
                        status,
                        String(outcome?.studentCount ?? '')
                    ];
                })
                // Resmî ÖÇ yüzdesi yalnızca ortak puan-ağırlıklı motordan gelir.
                // Motor verisi yoksa eski doğru-soru oranıyla yanıltıcı tablo üretme.
                : [];

            if (exportOptions.includeLearningOutcomes && learningRowsForPdf.length > 0) {
                doc.addPage();
                currentY = 30;
                
                // Modern başlık tasarımı
                doc.setFontSize(22);
                setFontSafe(doc, 'OpenSans', 'bold');
                doc.setTextColor(2, 59, 88);
                doc.text('Dersin öğrenme çıktıları ANALİZİ', 105, currentY, { align: 'center' });
                currentY += 15;
                
                // Alt başlık
                doc.setFontSize(12);
                setFontSafe(doc, 'OpenSans', 'normal');
                doc.setTextColor(107, 114, 128);
                doc.text(`Toplam ${learningRowsForPdf.length} Dersin Öğrenme Çıktısı Değerlendirildi`, 105, currentY, { align: 'center' });
                currentY += 10;
                
                // Dekoratif çizgi
                doc.setDrawColor(2, 59, 88);
                doc.setLineWidth(2);
                doc.line(30, currentY, 180, currentY);
                currentY += 25;
                
                const learningHeaders = ['ÖÇ', 'Açıklama', 'Oran', 'Durum', 'Öğrenci'];
                const learningColumnWidths = [16, 96, 22, 28, 18];
                
                currentY = drawTable(doc, learningRowsForPdf, learningHeaders, currentY, {
                    fontSize: 8,
                    columnWidths: learningColumnWidths,
                    startX: 12,
                    marginRight: 12,
                    rowColorMode: 'normal',
                    headerAlign: 'center',
                    statusTextColors: true,
                    cellHeight: 12,
                    headerBgColor: [2, 59, 88],
                    headerTextColor: [255, 255, 255],
                    cellPadding: 2,
                    keepTurkish: fontReady
                });
            }


            
            // Program Çıktıları Analizi Tablosu (sayfadaki generalRates öncelikli)
            const programRowsForPdf = (Array.isArray(generalRates?.programRates) && generalRates.programRates.length > 0)
                ? generalRates.programRates.map((outcome) => {
                    const avg = Number(outcome?.averageRate || 0);
                    let status = 'İyileştirilmeli (1)';
                    if (avg >= 75) status = 'Ortalamanın Üzerinde (4)';
                    else if (avg >= 60) status = 'Yeterli (3)';
                    else if (avg >= 50) status = 'Geliştiriliyor (2)';
                    return [
                        String(outcome?.code || `PÇ${outcome?.id || ''}`),
                        String(outcome?.description || ''),
                        `${avg.toFixed(2)}%`,
                        status,
                        String(outcome?.studentCount ?? '')
                    ];
                })
                // PÇ için de tek doğruluk kaynağı ortak puan-ağırlıklı motordur.
                : [];

            if (exportOptions.includeProgramOutcomes && programRowsForPdf.length > 0) {
                doc.addPage();
                currentY = 25;
                
                // Kompakt başlık tasarımı
                doc.setFontSize(18);
                setFontSafe(doc, 'OpenSans', 'bold');
                doc.setTextColor(2, 59, 88);
                doc.text('PROGRAM ÇIKTILARI ANALİZİ', 105, currentY, { align: 'center' });
                currentY += 10;
                
                // Kısa alt başlık
                doc.setFontSize(10);
                setFontSafe(doc, 'OpenSans', 'normal');
                doc.setTextColor(107, 114, 128);
                doc.text(`${programRowsForPdf.length} Program Çıktısı`, 105, currentY, { align: 'center' });
                currentY += 8;
                
                // İnce çizgi
                doc.setDrawColor(2, 59, 88);
                doc.setLineWidth(1);
                doc.line(20, currentY, 190, currentY);
                currentY += 15;
                
                const programHeaders = ['PÇ', 'Açıklama', 'Oran', 'Durum', 'Öğrenci'];
                const programColumnWidths = [16, 96, 22, 28, 18];
                
                currentY = drawTable(doc, programRowsForPdf, programHeaders, currentY, {
                    fontSize: 8,
                    columnWidths: programColumnWidths,
                    startX: 12,
                    marginRight: 12,
                    rowColorMode: 'normal',
                    headerAlign: 'center',
                    statusTextColors: true,
                    cellHeight: 12,
                    headerBgColor: [2, 59, 88],
                    headerTextColor: [255, 255, 255],
                    cellPadding: 2,
                    keepTurkish: fontReady
                });
            }
            

            

            
            // PDF çıktısı: önizleme veya indirme
            const timestamp = new Date().toISOString().split('T')[0];
            const courseNameSafe = sanitizeFilename(courseDisplayName);
            const fileName = `Sınav_Sonuçları_${courseNameSafe}_${timestamp}.pdf`;
            if (asPreview) {
                try {
                    const blobUrl = doc.output('bloburl');
                    setPreviewUrl(blobUrl);
                } catch (e) {
                    alert('Önizleme oluşturulamadı. Lütfen PDF indirmeyi deneyin.');
                }
            } else {
                doc.save(fileName);
            }
            
        } catch (error) {
            alert('PDF oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setIsGenerating(false);
            setIsPreviewing(false);
        }
    };

    return (
        <div className="pdf-export-layout">
            <div className="pdf-exporter">
            <div className="pdf-export-header">
                <h3>PDF Rapor İndirme</h3>
                <p>Seçeneklerinizi belirleyin ve PDF raporunuzu indirin</p>
            </div>
            
            <div className="pdf-export-options">
                {/* PDF'e özel sınav filtresi */}
                <div className="option-group">
                    <h4>PDF Sınav Filtresi</h4>
                    <div className="weights-filter">
                        <button
                            type="button"
                            className={`weight-chip ${!pdfExamFilter ? 'selected' : ''}`}
                            onClick={() => setPdfExamFilter('')}
                        >
                            Genel
                        </button>
                        {(Array.isArray(weights) && weights.length ? weights : (filteredResults?.weights || [])).map((w, i) => {
                            const t = w.type || w.exam_type;
                            return (
                                <button
                                    key={`${t}-${i}`}
                                    type="button"
                                    className={`weight-chip ${pdfExamFilter === t ? 'selected' : ''}`}
                                    onClick={() => setPdfExamFilter(t)}
                                >
                                    <span className="chip-label">{t}</span>
                                    <span className="chip-percent">{w.weight}%</span>
                                </button>
                            );
                        })}

                    </div>
                    <small>Bu seçim sadece PDF içeriğini filtreler.</small>
                </div>
                

                <div className="option-group">
                    <h4>Rapor Bölümleri</h4>
                    <div className="checkbox-group">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={exportOptions.includeStudentList}
                                onChange={(e) => setExportOptions(prev => ({
                                    ...prev,
                                    includeStudentList: e.target.checked
                                }))}
                            />
                            <span>Öğrenci Listesi ({studentCount} öğrenci)</span>
                        </label>
                        
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={exportOptions.includeLearningOutcomes}
                                onChange={(e) => setExportOptions(prev => ({
                                    ...prev,
                                    includeLearningOutcomes: e.target.checked
                                }))}
                            />
                            <span>Dersin öğrenme çıktıları Analizi ({learningOutcomesCount} çıktı)</span>
                        </label>
                        
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={exportOptions.includeProgramOutcomes}
                                onChange={(e) => setExportOptions(prev => ({
                                    ...prev,
                                    includeProgramOutcomes: e.target.checked
                                }))}
                            />
                            <span>Program Çıktıları Analizi ({programOutcomesCount} çıktı)</span>
                        </label>
                        
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={exportOptions.includeStudentAnalysis}
                                onChange={(e) => setExportOptions(prev => ({
                                    ...prev,
                                    includeStudentAnalysis: e.target.checked
                                }))}
                            />
                            <span>Öğrenci Özel Analiz</span>
                        </label>

                    </div>
                    
                    {/* Öğrenci Seçimi - Sadece Öğrenci Özel Analiz seçiliyse görünür */}
                    {exportOptions.includeStudentAnalysis && (
                        <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                            <h5 style={{ margin: '0 0 10px 0', color: '#495057' }}>Öğrenci Seçimi</h5>
                            <select 
                                value={selectedStudent} 
                                onChange={(e) => setSelectedStudent(e.target.value)}
                                style={{ 
                                    width: '100%', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    border: '1px solid #ced4da',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="">Öğrenci seçiniz...</option>
                                {availableStudents.map((student) => (
                                    <option key={student.id} value={student.id}>
                                        {student.number} - {student.name}
                                    </option>
                                ))}
                            </select>
                            <small style={{ color: '#6c757d', marginTop: '5px', display: 'block' }}>
                                {availableStudents.length > 0 
                                    ? `${studentCount} öğrenci mevcut (${availableStudents.length} toplam kayıt)` 
                                    : 'Öğrenci bulunamadı'}
                            </small>
                        </div>
                    )}
                </div>
            </div>
            
            <div className="pdf-export-actions">
                <button 
                    onClick={() => generatePDF(true)}
                    className={`pdf-export-button ${isPreviewing ? 'generating' : ''}`}
                    disabled={isPreviewing}
                >
                    {isPreviewing ? (
                        <>
                            <span className="loading-spinner"></span>
                            PDF Önizleme...
                        </>
                    ) : (
                        <>👁️ PDF Önizle</>
                    )}
                </button>
                <button 
                    onClick={() => generatePDF(false)}
                    className={`pdf-export-button ${isGenerating ? 'generating' : ''}`}
                    disabled={isGenerating}
                >
                    {isGenerating ? (
                        <>
                            <span className="loading-spinner"></span>
                            PDF Oluşturuluyor...
                        </>
                    ) : (
                        <>
                            📄 PDF İndir
                        </>
                    )}
                </button>
                
                {(isGenerating || isPreviewing) && (
                    <div className="generation-info">
                        <p>PDF raporunuz hazırlanıyor, lütfen bekleyin...</p>
                    </div>
                )}
            </div>

            {showOutcomePicker && (
                <div className="modal-overlay" onClick={() => setShowOutcomePicker(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setShowOutcomePicker(false)}>&times;</button>
                        <h3 className="response-modal-title">Bu sınavınızda hangi Dersin öğrenme çıktılarından soru sormak istiyorsunuz? Seçiniz.</h3>
                        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                            {availableLOs.length === 0 && (
                                <div className="empty-table-message">Listelenecek ders Dersin Öğrenme Çıktısı bulunamadı.</div>
                            )}
                            {availableLOs.map(lo => {
                                const oid = lo.id;
                                const label = `ÖÇ${lo.number || ''} - ${lo.name || ''}`;
                                const checked = selectedLOIds.includes(oid);
                                return (
                                    <label key={oid} className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                                setSelectedLOIds(prev => {
                                                    const set = new Set(prev);
                                                    if (e.target.checked) set.add(oid); else set.delete(oid);
                                                    return Array.from(set);
                                                });
                                            }}
                                        />
                                        <span>{label}</span>
                                    </label>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                            <button className="btn-cancel" onClick={() => setShowOutcomePicker(false)}>İptal</button>
                            <button className="reset-submit-btn" onClick={async () => {
                                if (!selectedLOIds || selectedLOIds.length === 0) return;
                                setShowOutcomePicker(false);
                                await generatePDF();
                            }}>Devam Et</button>
                        </div>
                    </div>
                </div>
            )}
            </div>
            <div className="pdf-preview-panel">
                <div className="pdf-preview-header">PDF Önizleme</div>
                {previewUrl ? (
                    <iframe title="pdf-preview" className="pdf-preview-iframe" src={previewUrl}></iframe>
                ) : (
                    <div className="pdf-preview-placeholder">Sağdaki “PDF Önizle” butonuna tıklayın.</div>
                )}
            </div>
        </div>
    );
};

export default PDFExporter;
